import React, { useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { cloneDeep, isEmpty, isNaN, toNumber } from 'lodash';
import moment from 'moment';
import { getTimeZoneAbbr } from '../../helpers/timezones';
import BobLoadingMask from '../../components/BobLoadingMask';
import NoSelectedSensorsScreen from '../../components/NoSelectedSensorsScreen';
import {Grid, GridNoRecords, GridColumn as Column, GridToolbar, GridColumnMenuWrapper} from '@progress/kendo-react-grid';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { filterBy, process as kendoProcess } from '@progress/kendo-data-query';
import { BooleanFilter, DateFilter, NumericFilter, Operators, TextFilter } from '@progress/kendo-react-data-tools';
import FilterPanel from '../../components/K2AFilterControl/FilterPanel';
import BooleanCellFilter from '../../components/CustomFilters/BooleanCellFilter';
import * as BobActions from '../../actions/bobActions';
import * as DashboardActions from '../../actions/dashboardActions';
import * as ConfigurationActions from '../../actions/configurationActions';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/styles';
import ConditionalFormatModal from '../../containers/Dashboard/Modals/ConditionalFormatModal';
import FormulaGridColumnModal from '../../components/FormulaGridColumnModal';

import classes from './RawDataTable.module.css';

const initialDataStateFilter = {
    logic:   'and',
    filters: []
};

const initialRawDataState = {
    filter: initialDataStateFilter,
    sort:   [],
    group:  [],
    skip:   0
};

const booleanCellFilterValues = [{ text: 'In Fault', operator: 'eq', value: true }, { text: 'No Fault', operator: 'eq', value: false }];
const defaultItem = { text: '(All)', operator: '' };

const FaultCellFilter = (props, selectedBooleanFilter, setSelectedBooleanFilter) => {
    return (
        <BooleanCellFilter
            {...props}
            data={booleanCellFilterValues}
            defaultItem={defaultItem}
            filterValue={selectedBooleanFilter}
            setFilterValue={setSelectedBooleanFilter}
        />
    );
};


const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: 'white',
        color:           'black',
        fontSize:        theme.typography.pxToRem(20),
        border:          '1px solid grey',
        borderRadius:    '5px',
        maxWidth:        '500px'
    }
}))(Tooltip);

const RawDataTable = ({
    activeConfiguration,
    activeEquipment,
    activeSystem,
    activeNavCompany,
    bobLoading,
    configuration,
    currentUser,
    dashboardEndTimeStamp,
    dashboardStartTimeStamp,
    exportRawData,
    fetchConfigurations,
    fetchRawData,
    gridDataState,
    gridFilter,
    gridFilterText,
    gridFields,
    healthscoreRawData,
    healthscoreRawDataMeta,
    history,
    isActive,
    isSiteView = false,
    newConfiguration,
    redirection,
    refreshRawDataGrid,
    ruleScoringConfig,
    ruleScoreKpis,
    saveConfiguration,
    selectedEquipmentRule,
    selectedRule,
    selectedWeatherSensors,
    setActiveConfiguration,
    setConfiguration,
    setExportRawData,
    setFormulaGridColumns,
    setGridDataState,
    setGridFilter,
    setGridFilterText,
    setGridFields,
    setHealthscoreRawData,
    setRedirection,
    setRefreshRawDataGrid,
    setRuleScoringConfiguration,
    setSelectedEquipmentRule,
    setSelectedRule,
    setSelectedRuleSensors,
    setSelectedWeatherSensors,
    viewKey,
    workbookModeEnabled,
    ...props
}) => {
    const rawDataGridRef = useRef(null);

    const [rule, setRule] = useState();
    const [columnNames, setColumnNames] = useState([]);
    const [exportColumnNames, setExportColumnNames] = useState([]);
    const [rawDataGridDataState, setRawDataGridDataState] = useState(initialRawDataState);
    const [rawDataGridFields, setRawDataGridFields] = useState([]);
    const [selectedBooleanFilter, setSelectedBooleanFilter] = useState(defaultItem);
    const [currentFormula, setCurrentFormula] = useState(null);
    const [formulaCellModalOpen, setFormulaCellModalOpen] = useState(false);
    const [formulaColumns, setFormulaColumns] = useState([]);
    const [formulaColumnIndex, setFormulaColumnIndex] = useState(0);
    const [formatObj,setFormatObj] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [formatList,setFormatList] = useState([]);
    const [formatKey,setFormatKey] = useState('');
    const [formatType,setFormatType] = useState('');
    const [formulaValues, setFormulaValues] = useState(null);
    const [ruleInfo, setRuleInfo] = useState({});
    const [rawData, setRawData] = useState([]);
    const _export = useRef(null);
    const queryValues = queryString.parse(history.location.search);

    const styleMap = {
        'yellow-cell': {background: '#ffefaf',color: '#783f05'},
        'green-cell':  {background: '#b7e1cd',color: '#274e13'},
        'grey-cell':   {background: '#d9d9d9',color: '#434343'},
        'red-cell':    {background: '#fcd6d6',color: '#85200c'}
    };

    useEffect(() => {
        const init = async () => {
            await fetchRawData(queryValues.company, activeEquipment?.id, activeSystem?.id, rule?.ruleId, dashboardStartTimeStamp.format(), dashboardEndTimeStamp.format());
        };

        if (((activeEquipment && activeEquipment.id) || (activeSystem && activeSystem.id)) && rule && rule.ruleId) init();
    }, [dashboardEndTimeStamp, dashboardStartTimeStamp]);

    useEffect(() => {
        if (formulaValues) {
            addFormulaColumn(formulaValues);
        }
    }, [formulaValues]);

    useEffect(() => {
        const init = () => {
            setRule(selectedEquipmentRule);
            handleClickRule(selectedEquipmentRule);
        };
        if (((activeEquipment && activeEquipment.id) || (activeSystem && activeSystem.id)) && selectedEquipmentRule && selectedEquipmentRule.ruleId) {
            init();
        }
        else {
            setRule({});
            setRawData([]);
            setHealthscoreRawData([], {});
        }
    }, [activeEquipment, selectedEquipmentRule, activeSystem]);
    
    useEffect(() => {
        const init = () => {
            setRule(selectedRule);
        };
        if (selectedRule && selectedRule.ruleId) init();
    }, [selectedRule]);

    useEffect(() => {
        if (exportRawData) {
            handleExport();
            setExportRawData(false);
        }
    }, [exportRawData]);

    useEffect(() => {
        if (refreshRawDataGrid && ((activeEquipment && activeEquipment.id) || (activeSystem && activeSystem.id)) && selectedEquipmentRule && selectedEquipmentRule.ruleId) {
            handleClickRule(selectedEquipmentRule);
        }
        setRefreshRawDataGrid(false);
    }, [refreshRawDataGrid]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            setRawDataGridDataState({...rawDataGridDataState, filter: initialDataStateFilter});
            setGridFilterText('');
        }
    }, [workbookModeEnabled]);

    const addFormulaColumn = formula => {
        const currentFormulaList = cloneDeep(formulaColumns);
        const foundColumn = currentFormulaList.find(c => c.fieldName === formula.fieldName);
        if (!foundColumn) {
            const newFormulaList = [...currentFormulaList, formulaValues];
            setFormulaColumns(newFormulaList);
            const newColumn = {
                name:       formulaValues.cellTitle,
                title:      formulaValues.cellTitle,
                field:      formulaValues.fieldName,
                filter:     'numeric',
                format:     '{0:n}',
                isFormula:  true,
                orderIndex: formulaValues.cellIndex
            };

            let columnList = [...columnNames, newColumn];
            let exportColumnList = [...exportColumnNames, newColumn];
            setColumnNames(columnList);
            setExportColumnNames(exportColumnList);
            if (!isSiteView) {
                configuration.columns = columnList;
                configuration.selectedFormulaColumns = newFormulaList;
                setConfiguration(configuration);
            }
        }
        else {
            const index = currentFormulaList.findIndex(c => c.fieldName === formula.fieldName);
            currentFormulaList[index] = formula;

            // grid columns
            try {
                columnNames.find(c => c.field === formula.fieldName).name = formula.cellTitle;
                columnNames.find(c => c.field === formula.fieldName).title = formula.cellTitle;
            }
            catch {
                // no need to do anything
            }

            // export columns
            try {
                exportColumnNames.find(c => c.field === formula.fieldName).name = formula.cellTitle;
                exportColumnNames.find(c => c.field === formula.fieldName).title = formula.cellTitle;
            }
            catch {
                // no need to do antying
            }

            setFormulaColumns(currentFormulaList);
            configuration.columns = columnNames;
            configuration.selectedFormulaColumns = currentFormulaList;
            setConfiguration(configuration);
        }

        const dataSet = addFormulaToGridDataSet(formulaValues, formulaValues.fieldName, rawData);
        setRawData(dataSet);
    };

    const addFormulaToGridDataSet = (formula, fieldName, gridData) => {
        const newDataSet = cloneDeep(gridData);
        let formulaResult = 0;
        newDataSet.map(data => {
            switch (formula.operator.operator) {
                case '+':
                    formulaResult = parseFloat(data[formula.columnOne.field]) + parseFloat(data[formula.columnTwo.field]);
                    break;
                case '-':
                    formulaResult = parseFloat(data[formula.columnOne.field]) - parseFloat(data[formula.columnTwo.field]);
                    break;
                case '*':
                    formulaResult = parseFloat(data[formula.columnOne.field]) * parseFloat(data[formula.columnTwo.field]);
                    break;
                case '/':
                    formulaResult = parseFloat(data[formula.columnOne.field]) / parseFloat(data[formula.columnTwo.field]);
                    break;
                default:
                    formulaResult = parseFloat(data[formula.columnOne.field]) + parseFloat(data[formula.columnTwo.field]);
                    break;
            }
            return data[formula.fieldName] = formulaResult.toFixed(formula.precision);
        });
        return newDataSet;
    };

    useEffect(() => {
        const init = () => {
            const { viewData } = activeConfiguration;
            if (viewData !== null && !isEmpty(viewData)) {
                if (!isEmpty(viewData.selectedRule)) {
                    setSelectedRule(viewData.selectedRule);
                    setSelectedEquipmentRule(viewData.selectedRule);
                    handleClickRule(viewData.selectedRule);
                }

                if (!isEmpty(viewData.selectedSensors)) {
                    setSelectedRuleSensors(viewData.selectedSensors);
                }
            }
        };
        if (activeConfiguration && activeConfiguration.viewData) init();
    }, [activeConfiguration]);

    useEffect(() => {
        if (selectedRule && selectedRule.ruleId && !isEmpty(healthscoreRawDataMeta)) {
            const info = healthscoreRawDataMeta.info.rules.find(r => r.id === selectedRule.ruleId);
            if (info) {
                setRuleInfo(info);
            }
        }
        else {
            setRuleInfo({});
        }
        
        let columns = [
            { name: 'Start Date', field: 'start', title: 'Start Date', filter: 'date', width: '200px', fault: false },
            { name: 'End Date', field: 'end', title: 'End Date', filter: 'date', width: '200px', fault: false },
            { name: 'Fault', field: 'fault', title: 'Fault', filter: 'boolean', width: '150px', fault: true },
            { name: 'Score', field: 'score', title: 'Score', filter: 'numeric', width: '150px', fault: false }
        ];

        let rawDataCopy = cloneDeep(healthscoreRawData);

        if (rawDataCopy.length > 0) {
            // eslint-disable-next-line no-sequences
            const keys = Object.entries(rawDataCopy[0]).sort().reduce( (o,[k,v]) => (o[k]=v,o), {} );
            Object.keys(keys).forEach(item => {
                let id = item.split('_');
                id = id[1];
                if (item.startsWith('ruleName') || item.startsWith('ruleFvoSensorName') || item.startsWith('sensorName')) {
                    Object.keys(rawDataCopy[0]).forEach(point => {
                        if (point === `ruleFvoSensorVal_${ id }` || point === `sensorVal_${ id }`) {
                            let filterType = 'numeric';
                            if (point.includes('ruleVal') || point.includes('temp') || point.includes('humidity') || point.includes('pressure')) {
                                filterType = 'numeric';
                            }
                            else if (typeof rawDataCopy[0][point] === 'boolean' ||
                            rawDataCopy[0][point]?.toString().toLowerCase() === 'false' ||
                            rawDataCopy[0][point]?.toString().toLowerCase() === 'true') {
                                filterType = 'boolean';
                            }
                            else if (!isNaN(rawDataCopy[0][point])) {
                                filterType = 'numeric';
                            }
                            else if (isEmpty(rawDataCopy[0][point]) || rawDataCopy[0][point] === 'null') {
                                filterType = 'text';
                            }

                            columns.push({
                                name:   rawDataCopy[0][item],
                                title:  rawDataCopy[0][item],
                                field:  point,
                                filter: filterType,
                                format: filterType === 'numeric' ? '{0:n}' : null,
                                fault:  false,
                                width:  '200px'
                            });
                        }
                    });
                }
            });
        }
        setColumnNames(columns);
        setExportColumnNames(columns);
        setRawData(healthscoreRawData);

    }, [healthscoreRawData]);

    useEffect(() => {
        const columns = columnNames.map(column => {
            const filterType = column.filter === 'text' ? TextFilter : column.filter === 'numeric' ? NumericFilter : column.filter === 'date' ? DateFilter : BooleanFilter;
            const operatorList = column.filter === 'text' ? Operators.text : column.filter === 'numeric' ? Operators.numeric : column.filter === 'date' ? Operators.date : Operators.boolean;
            return {
                name:      column.field,
                label:     column.title ? column.title : column.name,
                filter:    filterType,
                operators: operatorList
            };
        });
        setRawDataGridFields(columns);
    }, [columnNames]);

    const BuildFaultCellFilterCell = props => {
        return FaultCellFilter(props, selectedBooleanFilter, setSelectedBooleanFilter);
    };

    const handleClose = () => {
        setShowModal(false);
    };

    const handleExport = event => {
        const options = _export.current.workbookOptions();
        const rows = options.sheets[0].rows;
        rows.forEach((row,rindex) => {
            if (row.type === 'data') {
                row.cells.forEach((cell, colindex) => {
                    if (colindex === 0) {
                        formatTimeStampCell(cell, colindex);
                    }
                    else formatDataCell(cell, colindex);
                });
            }
        });
        _export.current.save(options);
    };

    const formatTimeStampCell = (cell, colindex) => {
        const field = exportColumnNames[colindex].field;
        const conditions = formatObj[field];
        let _className = '';
        const datatype = 'date';
        let zone = moment.tz.guess();
        let fd = moment(cell.value).format('YYYY-MM-DD h:mm A');
        const timeDisplay = fd + ' ' + getTimeZoneAbbr(cell.value, zone);
        if (conditions && cell.value !== null && cell.value !== '' && cell.value !== undefined) {
            _className = parseCondition(conditions, fd, datatype);
        }
        if (_className) {
            cell.background = styleMap[_className].background;
            cell.color = styleMap[_className].color;
        }
        cell.value = timeDisplay;
    };

    const formatDataCell = (cell, colindex) => {
        const field = exportColumnNames[colindex].field;
        const conditions = formatObj[field];
        let _className = '';
        const datatype = exportColumnNames[colindex].filter;
        if (conditions && cell.value !== null && cell.value !== '' && cell.value !== undefined) {
            _className = parseCondition(conditions, cell.value, datatype);
        }
        if (_className) {
            cell.background = styleMap[_className].background;
            cell.color = styleMap[_className].color;
        }
        if (datatype === 'numeric') {
            cell.format = '#,###,##0.##########';
        }
    };

    //Ref - https://www.geeksforgeeks.org/interval-tree/
    class Interval
    {
        constructor()
        {
            this.low = 0;
            this.high = 0;
        }
    }

    class ITNode
    {
        // 'i' could also be a normal variable
        constructor()
        {
            this.max = 0;
            this.left = null;
            this.right = null;
            this.i = null;
        }
    }

    // A utility function to create a new node
    function newNodeDouble(l, h)
    {
        var temp = new Interval();
        temp.low = l;
        temp.high = h;
        return temp;
    }

    // A utility function to create a new node
    function newNodeSingle(i)
    {
        var temp = new ITNode();
        temp.i = i;
        temp.max = i.high;
        temp.left = temp.right = null;
        return temp;
    }

    // A utility function to insert a new
    // Interval Search Tree Node. This is
    // similar to BST Insert. Here the
    // low value of interval is used to
    // maintain BST property
    function insert(root, i)
    {

        // Base case: Tree is empty,
        // new node becomes root
        if (root == null)
            return newNodeSingle(i);

        // Get low value of interval at root
        var l = root.i.low;

        // If root's low value is smaller,
        // then new interval goes to left subtree
        if (i.low < l)
            root.left = insert(root.left, i);

        // Else, new node goes to right subtree.
        else
            root.right = insert(root.right, i);

        // Update the max value of this
        // ancestor if needed
        if (root.max < i.high)
            root.max = i.high;
        return root;
    }

    // A utility function to check if given
    // two intervals overlap
    function doOVerlap(i1, i2)
    {
        if (i1.low < i2.high && i2.low < i1.high)
            return true;
        return false;
    }

    // The main function that searches a given
    // interval i in a given Interval Tree.
    function overlapSearch(root, i)
    {

        // Base Case, tree is empty
        if (root == null)
            return null;

        // If given interval overlaps with root
        if (doOVerlap(root.i, i))
            return root.i;

        // If left child of root is present
        // and max of left child is greater
        // than or equal to given interval,
        // then i may overlap with an interval
        // is left subtree
        if (root.left != null &&
       root.left.max >= i.low)
            return overlapSearch(root.left, i);

        // Else interval can only
        // overlap with right subtree
        return overlapSearch(root.right, i);
    }

    // This function prints all conflicting
    // appointments in a given array of apointments.
    function printConflicting(appt, n)
    {

        // Create an empty Interval Search
        // Tree, add first appointment
        var root = null;
        root = insert(root, appt[0]);

        // Process rest of the intervals
        for (var i = 1; i < n; i++)
        {

            // If current appointment conflicts
            // with any of the existing intervals,
            // print it
            var res = overlapSearch(root, appt[i]);

            if (res != null) {
                appt[i].conflicted = true;
                res.conflicted = true;
            }
            // Insert this appointment
            root = insert(root, appt[i]);
        }
    }

    const validateConditions = ()=>{
        const intervals =[];
        formatList.forEach(c => {
            if (c.operator === 'Greater Than') {
                intervals.push(newNodeDouble(c.field1,Number.MAX_SAFE_INTEGER));
            }
            else  if (c.operator === 'Less Than') {
                intervals.push(newNodeDouble(0,c.field1));
            }
            else  if (c.operator === 'Between') {
                intervals.push(newNodeDouble(c.field1,c.field2));
            }
            else  if (c.operator === 'Equals') {
                intervals.push(newNodeDouble(c.field1,c.field1));
            }
        });
        printConflicting(intervals, intervals.length);
        return intervals;
    };

    const handleSubmit = (data,key) =>{
        if (formatType === 'numeric') {
            let gaps = validateConditions();
            let formatlist = formatList;
            let conflict = false;
            gaps.forEach((c,i)=>{
                formatlist[i].conflicted = c.conflicted?true:false;
                if (c.conflicted)
                    conflict = true;
            });
            if (conflict) {
                setFormatList([...formatlist]);
                return false;
            }
        }
        let obj = formatObj;
        obj[formatKey] = JSON.parse(JSON.stringify(formatList));
        setConfiguration({ ...configuration, conditionalFormat: obj });
        setFormatObj(obj);
        setColumnNames([...columnNames]);
        return true;
    };

    const updateFormat = (data,i,key) =>{
        let formatlist = formatList && formatList.length>0?formatList:[];
        if (formatlist[i]) {
            formatlist[i][key] = data;
        }
        else {
            let obj = {};
            obj[key] = data;
            formatlist.push(obj);
        }
        setFormatList([...formatlist]);
    };

    const addItem = ()=>{
        let formatlist = formatList && formatList.length>0?formatList:[];
        formatlist.push({'operator': '','condition': ''});
        setFormatList([...formatlist]);
    };

    const removeItem = (index) =>{
        let formatlist = formatList && formatList.length>0?formatList:[];
        formatlist.splice(index,1);
        setFormatList([...formatlist]);
    };

    const handleClickRule = async dataItem => {
        // configuration changes
        setActiveConfiguration({});

        let ruleSensors = [];
        
        let ruleIds = [dataItem.ruleId];

        let currentConfig = ruleScoringConfig;
        let promises = [];

        // This block of code manages the rule config storage
        // We look to see if we have a current configuration
        if (!isEmpty(currentConfig.current)) {

            // If we have a current config we check to see if the current config is the rule selected
            if (ruleScoringConfig.current.ruleId === ruleIds[0]) {

                promises.push(fetchRawData(queryValues.company, activeEquipment?.id, activeSystem?.id, dataItem.ruleId, dashboardStartTimeStamp.format(), dashboardEndTimeStamp.format()));

            // We check if the rule selected is one of the previous configurations
            } else if (ruleScoringConfig.previous.find(config => config.ruleId === ruleIds[0])) {

                // If so, we use the sensors from that configuration, set it as the new current, and remove it from the previous array.
                const existingRule = currentConfig.previous.find(config => config.ruleId === ruleIds[0]);
                const currentRule = currentConfig.current;

                currentConfig.previous.push(currentRule);
                currentConfig.previous = currentConfig.previous.filter(item => item.ruleId !== ruleIds[0]);

                currentConfig.current = {
                    ruleId:        ruleIds[0],
                    sensorIds:     existingRule.sensorIds,
                    ruleSensorIds: existingRule.ruleSensorIds
                };
                setSelectedRuleSensors(existingRule.sensorIds);
                setRuleScoringConfiguration(currentConfig);
                ruleSensors = existingRule.sensorIds;

                promises.push(fetchRawData(queryValues.company, activeEquipment?.id, activeSystem?.id, dataItem.ruleId, dashboardStartTimeStamp.format(), dashboardEndTimeStamp.format()));

            // This scenario is what happens when we have a current configuration but select a new rule.
            } else {
                const ids = [];
                ruleSensors = ids;

                const currentRule = currentConfig.current;
                currentConfig.previous.push(currentRule);

                currentConfig.current = {
                    ruleId:        ruleIds[0],
                    sensorIds:     ids,
                    ruleSensorIds: ids
                };

                setRuleScoringConfiguration(currentConfig);

                promises.push(fetchRawData(queryValues.company, activeEquipment?.id, activeSystem?.id, dataItem.ruleId, dashboardStartTimeStamp.format(), dashboardEndTimeStamp.format()));

            }

        // This scenario is what happens when there has been no current configuration, initial load.
        } else {
            const ids = [];

            setSelectedRuleSensors(ids);
            ruleSensors = ids;

            currentConfig.current = {
                ruleId:        ruleIds[0],
                sensorIds:     ids,
                ruleSensorIds: ids
            };

            promises.push(fetchRawData(queryValues.company, activeEquipment?.id, activeSystem?.id, dataItem.ruleId, dashboardStartTimeStamp.format(), dashboardEndTimeStamp.format()));

        }

        if (!isSiteView) {
            let updateConfiguration = cloneDeep(configuration);
            updateConfiguration.selectedRule = dataItem;
            updateConfiguration.selectedSensors = ruleSensors;
            setConfiguration(updateConfiguration);
        }
        setSelectedRule({ ...dataItem, sensorIds: ruleSensors });

        await Promise.all(promises.map(p => p.catch(err => err)));
    };

    const ColumnMenu = (props) => {

        const createConditionalFormat =()=>{
            props.onCloseMenu();
            let formats = [];
            if (formatObj[props.column.field]) {
                formats = formatObj[props.column.field];
            }
            if (props.column.field !== 'start' && props.column.field !== 'end') {
                let col =columnNames.filter(c=>c.field === props.column.field);
                if (col && col.length > 0) {
                    setFormatType(col[0].filter);
                }
            }
            else {
                setFormatType('date');
            }
            setFormatList(JSON.parse(JSON.stringify(formats)));
            setFormatKey(props.column.field);
            setShowModal(true);
        };


        const clearColumnFormat =()=>{
            props.onCloseMenu();
            let format = formatObj;
            if (format[props.column.field]) {
                delete format[props.column.field];
            }
            setFormatObj(format);
            setFormatList([]);
        };

        const clearTableFormat =()=>{
            props.onCloseMenu();
            setFormatObj({});
            setFormatList([]);
        };

        const addCalculatedColumn = () => {
            setCurrentFormula(null);
            const colIndex = rawDataGridRef.current._columns.find(c => c.field === props.column.field).orderIndex;
            if (!colIndex) {
                setFormulaColumnIndex(rawDataGridRef.current._columns.length);
            }
            else {
                setFormulaColumnIndex(colIndex);
            }

            props.onCloseMenu();
            setFormulaCellModalOpen(true);
        };

        const editCalculatedColumn = () => {
            const editFormula = formulaColumns.find(f => f.fieldName === props.column.field);
            if (editFormula) {
                setCurrentFormula(editFormula);
                setFormulaCellModalOpen(true);
            }
            props.onCloseMenu();
        };

        const remomveCalculatedColumn = () => {
            const updatedColumns = columnNames.filter(c => c.field !== props.column.field);
            setColumnNames(updatedColumns);

            const updatedExportColumns = exportColumnNames.filter(c => c.field !== props.column.field);
            setExportColumnNames(updatedExportColumns);
            setFormulaGridColumns(updatedColumns);

            const currentFormulaList = cloneDeep(formulaColumns);
            const updatedColumnList = currentFormulaList.filter(c => c.fieldName !== props.column.field);
            setFormulaColumns(updatedColumnList);
            configuration.columns = updatedColumns;
            configuration.selectedFormulaColumns = updatedColumnList;
            setConfiguration(configuration);
            props.onCloseMenu();
        };

        return (
            <div>
                <div style={{width: 'auto'}} className={classes.BobActionMenuFormat}>
                    <button
                        onClick={createConditionalFormat} 
                        className={`MuiButtonBase-root MuiButton-root MuiButton-text ${classes.BobActionFormatItem}`} 
                        tabIndex="0"
                        type="button"
                    >
                        <span className="MuiButton-label">
                            <i className={`fa fa-plus ${ classes.BobActionMenuIcon }`} />
                            Conditional Formatting
                        </span>
                    </button>
                    <button
                        onClick={clearColumnFormat} 
                        className={`MuiButtonBase-root MuiButton-root MuiButton-text ${classes.BobActionFormatItem}`} 
                        tabIndex="0"
                        type="button"
                    >
                        <span className="MuiButton-label">
                            <i className={`fas fa-redo-alt ${ classes.BobActionMenuIcon }`} />
                            Clear Column Formatting
                        </span>
                    </button>
                    <button
                        onClick={clearTableFormat} 
                        className={`MuiButtonBase-root MuiButton-root MuiButton-text ${classes.BobActionFormatItem}`} 
                        tabIndex="0"
                        type="button"
                    >
                        <span className="MuiButton-label">
                            <i className={`fas fa-redo-alt ${ classes.BobActionMenuIcon }`} />
                            Clear Table Formatting
                        </span>
                    </button>
                    {
                        columnNames.filter(c => c.filter === 'numeric').length > 1 ?
                            <button
                                onClick={addCalculatedColumn} 
                                className={`MuiButtonBase-root MuiButton-root MuiButton-text ${ classes.BobActionFormatItem }`} 
                                tabIndex="0"
                                type="button"
                            >
                                <span className="MuiButton-label">
                                    <i className={`fas fa-columns ${ classes.BobActionMenuIcon }`} />
                                    Add Formula Column
                                </span>
                            </button>
                            : null
                    }
                    {
                        props.isFormula ?
                            <button
                                onClick={editCalculatedColumn} 
                                className={`MuiButtonBase-root MuiButton-root MuiButton-text ${ classes.BobActionFormatItem }`} 
                                tabIndex="0"
                                type="button"
                            >
                                <span className="MuiButton-label">
                                    <i className={`fa fa-edit ${ classes.BobActionMenuIcon }`} />
                                    Edit Formula Column
                                </span>
                            </button> : null
                    }
                    {
                        props.isFormula ?
                            <button
                                onClick={remomveCalculatedColumn} 
                                className={`MuiButtonBase-root MuiButton-root MuiButton-text ${classes.BobActionFormatItem}`} 
                                tabIndex="0"
                                type="button"
                            >
                                <span className="MuiButton-label">
                                    <i className={`fa fa-times ${ classes.BobActionMenuIcon }`} />
                                    Remove Formula Column
                                </span>
                            </button>
                            : null
                    }
                </div>

            </div>
        );
    };

    const onRawDataGridDataStateChange = e => {
        setRawDataGridDataState(e.dataState);
    };

    const onColumnReorder = e => {
        const orderedColumns = e.columns.sort((a, b) => a.orderIndex - b.orderIndex).map((column, index) => {
            return {
                name:       column.title,
                field:      column.field,
                filter:     column.filter,
                locked:     column.locked ? column.locked : false,
                fault:      column.fault,
                orderIndex: index
            };
        });
        setExportColumnNames(orderedColumns);
        if (!isSiteView) {
            setConfiguration({ ...configuration, columns: orderedColumns });
        }
    };

    const timeStampCell = props => {
        const conditions = formatObj[props.field];
        let _className = '';
        const datatype = 'date';
        let zone = moment.tz.guess();
        let fd =  moment(props.dataItem[props.field]).format('YYYY-MM-DD h:mm A');
        const timeDisplay = props.dataItem[props.field] !== null ? fd + ' ' + getTimeZoneAbbr(props.dataItem[props.field], zone) : null;
        if (conditions && props.dataItem[props.field] !== null && props.dataItem[props.field] !== '' && props.dataItem[props.field] !== undefined) {
            _className = parseCondition(conditions, fd, datatype);
        }
        return (
            <td className={`${props.className} ${_className}`} style={props.style}>
                {timeDisplay}
            </td>
        );
    };

    const parseCondition = (conditions, value, datatype) => {
        let style = '';
        if (datatype === 'numeric') {
            conditions.forEach(c => {
                if (c.operator === 'Greater Than') {
                    if (Number(value) > c.field1) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Less Than') {
                    if (Number(value) < c.field1) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Equals') {
                    if (Number(value) === c.field1) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Between') {
                    if ((Number(value) >= c.field1 && Number(value) <= c.field2) || (Number(value) <= c.field1 && Number(value) >= c.field2)) {
                        style = getStyle(c.condition);
                    }
                }
            });
        }
        else if (datatype === 'boolean') {
            conditions.forEach(c => {
                if (c.operator === 'Equals') {
                    if (getBoolean(value) === getBoolean(c.field1)) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Does not Equal') {
                    if (getBoolean(value) !== getBoolean(c.field1)) {
                        style = getStyle(c.condition);
                    }
                }
            });
        }
        else if (datatype === 'date') {
            conditions.forEach(c => {
                if (c.operator === 'Greater Than') {
                    if (moment(value).isAfter(moment(c.field1))) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Less Than') {
                    if (moment(value).isBefore(moment(c.field1))) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Equals') {
                    if (moment(value).isSame(moment(c.fields1))) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Between') {
                    if (moment(value).isBetween(moment(c.field1, c.field2))) {
                        style = getStyle(c.condition);
                    }
                }
            });
        }
        else if (datatype === 'string' || datatype === 'text') {
            conditions.forEach(c => {
                if (c.operator === 'Contains') {
                    if (value.indexOf(c.field1) !== -1) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Does not contain') {
                    if (value.indexOf(c.field1) === -1) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Equals') {
                    if (value === c.field1 ) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Does not Equal') {
                    if (value !== c.field1 ) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Starts with') {
                    if (value.startsWith(c.field1 )) {
                        style = getStyle(c.condition);
                    }
                }
                else if (c.operator === 'Ends with') {
                    if (value.endsWith(c.field1 )) {
                        style = getStyle(c.condition);
                    }
                }
            });
        }
        return style;
    };

    const valueHeaderCell = (props) => {
        if (props.field) {
            const headerValue = props.field.startsWith('ruleName') ? `Rule - ${props.title}` : props.field === 'ruleFvo' ? 'Formula Value Output' : props.field.startsWith('sensor') ? props.title.startsWith('*') ? props.title : `*${props.title}` : props.title !== null ? props.title : 'Unknown Sensor';
            return (
                <HtmlTooltip
                    title={
                        <span className={classes.Tooltip}>
                            {props.title}
                        </span>
                    }
                    placement="top"
                >
                    <span className="k-cell-inner">
                        <span role="none" className="k-link" onClick={props.onClick}>
                            <span title={props.title}>{headerValue}</span>
                            {props.children}
                        </span>
                        <GridColumnMenuWrapper
                            {...props.columnMenuWrapperProps}
                        />
                    </span>
                </HtmlTooltip>
            );
        }
        else {
            return '';
        }
    };
    
    const valueCell = props => {
        const { dataItem, field } = props;
        const conditions = formatObj[field];
        let className = '';
        const datatype = columnNames.filter(c => c.field === field)[0].filter;
        if (conditions && dataItem[field] !== null && dataItem[field] !== '' && dataItem[field] !== undefined) {
            className = parseCondition(conditions,dataItem[field],datatype);
        }

        if (field) {
            if (field.startsWith('ruleName')) {
                let value = props.dataItem[field];
                if (props.dataItem[field] !== null) {
                    value = props.dataItem[field];
                }

                return (
                    <td style={{ textTransform: 'capitalize' }} className={className}>
                        {(value !== null && value !== 'null' && value !== '' && value !== undefined) ? value : ''}
                    </td>
                );
            } else if (field === 'ruleFvo') {
                let fvoValue = dataItem[field] !== null && dataItem[field] !== '' && dataItem[field] !== undefined ? dataItem[field].toString() : 'No Data';
                if (!isNaN(fvoValue) && fvoValue?.toLowerCase() !== 'true' && fvoValue?.toLowerCase() !== 'false') {
                    fvoValue = Math.round(toNumber(fvoValue) * 10000) / 10000;
                }
                return (
                    <td style={{ textTransform: 'capitalize' }} className={className}>
                        {fvoValue}
                    </td>
                );
            } else {
                return (
                    <td style={{ textTransform: 'capitalize' }} className={className}>
                        {(dataItem[field] !== null && dataItem[field] !== '' && dataItem[field] !== undefined) ? dataItem[field].toString() : 'No Data'}
                    </td>
                );
            }
        }
        else {
            return '';
        }
    };

    const faultCell = props => {
        const title = buildFvoTooltip(props.dataItem);
        const className = `material-symbols-outlined ${props.dataItem[props.field] ? 'red' : 'green'}-status`;
        const icon = props.dataItem[props.field] ? 'warning' : 'task_alt';
        return (
            <HtmlTooltip
                title={title}
                placement="top-start"
            >
                <td className={props.className} style={props.style}>
                    <span className={className}>
                        {icon}
                    </span>
                </td>

            </HtmlTooltip>
        );
    };

    const replaceSensorTypeIdWithName = (formula, sensorFilters, useValues, props) => {
        
        // don't need to show any of the comments that come before the formula itself
        const returnLocation = formula.toLowerCase().indexOf('return');
        let parsedFormula = returnLocation === 0 ? formula : formula.substring(returnLocation);

        const split = parsedFormula.split(' ');

        split.forEach((item, index) => {
            if (item.includes('sensors[')) {
                const id = item.match(/"(.*?)"/g) || item.match(/'(.*?)'/g);
                const selectedId = id[0].replaceAll('"', '').replaceAll('\'', '');
                const sensor = sensorFilters.find(sensor => sensor.id === selectedId);

                if (sensor) {
                    let replacementValue = sensor.sensorTypeName;
                    if (useValues) {
                        if (!isNaN(props[`ruleFvoSensorVal_${selectedId}`])) {
                            replacementValue = Math.round(toNumber(props[`ruleFvoSensorVal_${selectedId}`] * 10000) / 10000);
                        }
                        else {
                            replacementValue = replacementValue = props[`ruleFvoSensorVal_${selectedId}`];
                        }
                        
                    }
                    parsedFormula = parsedFormula.replace(`sensors['${sensor.id}']`, replacementValue);
                }
            }
        });

        return parsedFormula.replace('return ', '');
    };

    const buildFvoTooltip = props => {
        let formattedFaultCode;
        let faultCodeTooltipText;
        let formattedFormulaCode;
        let formattedFormulaValue;
        let formulaCodeTooltipText;
        let ruleAggregateTooltip;
        let sensorTypesTooltip;

        const {
            faultCode, 
            formulaCode,
            ruleAggregateCount, 
            ruleAggregateDuration, 
            ruleAggregateType, 
            sensorFilters
        } = ruleInfo;

        if (faultCode) {
            formattedFaultCode = replaceSensorTypeIdWithName(faultCode, sensorFilters);
            if (formattedFaultCode.includes('fvo')) {
                let fvoValue = props.ruleFvo;
                if (fvoValue?.toLowerCase() !== 'true' && fvoValue?.toLowerCase() !== 'false') {
                    fvoValue = Math.round(props.ruleFvo * 10000) / 10000;
                }
                formattedFaultCode = formattedFaultCode.replace('fvo', fvoValue);
            }

            faultCodeTooltipText = 
                <div>
                    <div>Fault Logic:</div>
                    <div className={classes.FaultTooltipItem} style={{color: 'blue'}}>{formattedFaultCode}</div>
                </div>
            ;
        }

        if (formulaCode) {
            formattedFormulaCode = replaceSensorTypeIdWithName(formulaCode, sensorFilters);
            formattedFormulaValue = replaceSensorTypeIdWithName(formulaCode, sensorFilters, true, props);

            let fvoValue = props.ruleFvo;
            if (fvoValue?.toLowerCase() !== 'true' && fvoValue?.toLowerCase() !== 'false') {
                fvoValue = Math.round(props.ruleFvo * 10000) / 10000;
            }

            formulaCodeTooltipText = 
                <div>
                    <div>Formula Code:</div>
                    <div className={classes.FaultTooltipItem}>{formattedFormulaCode}</div>
                    <div className={classes.FaultTooltipItem} style={{color: 'blue'}}>{formattedFormulaValue} = {fvoValue}</div>
                </div>
            ;
        }
        
        if (ruleAggregateType?.toLowerCase() !== 'none') {
            ruleAggregateTooltip =
                <div>
                    <div>Rule Aggregate:</div>
                    <div className={classes.FaultTooltipItem}>Type: {ruleAggregateType}</div>
                    <div className={classes.FaultTooltipItem}>Duration: {ruleAggregateDuration} - Count: {ruleAggregateCount}</div>
                </div>;
        }
        else {
            ruleAggregateTooltip = 
                <div>
                    <div>Rule Aggregate:</div>
                    <div className={classes.FaultTooltipItem}>Type: {ruleAggregateType}</div>
                </div>;
        }

        if (sensorFilters) {
            sensorTypesTooltip = 
                <div>
                    <div>Sensor Aggregates:</div>
                    {
                        sensorFilters.map(sensor => 
                            <React.Fragment>
                                <div className={classes.FaultTooltipItem}>Sensor: {sensor.sensorTypeName}</div>
                                <div className={classes.FaultTooltipItem}>Type: {sensor.sensorAggregateType}{sensor.sensorAggregateType.toLowerCase() !== 'none' ? ` - Duration: ${sensor.sensorAggregateDuration}`: ''}</div>
                            </React.Fragment>
                        )
                    }
                </div>;
        }

        const tooltip = (
            <React.Fragment>
                {formulaCodeTooltipText?.length !== 0 ? formulaCodeTooltipText : null}
                {faultCodeTooltipText?.length !== 0 ? faultCodeTooltipText : null}
                {ruleAggregateTooltip?.length !== 0 ? ruleAggregateTooltip : null}
                {sensorTypesTooltip?.length !== 0 ? sensorTypesTooltip : null}
            </React.Fragment>    
        );
        return tooltip;
    };

    const getStyle = (condition) => {
        if (!condition) {
            return '';
        }
        else if (condition.indexOf('Yellow') !== -1) {
            return 'yellow-cell';
        }
        else if (condition.indexOf('Green') !== -1) {
            return 'green-cell';
        }
        else if (condition.indexOf('Grey') !== -1) {
            return 'grey-cell';
        }
        else if (condition.indexOf('Red') !== -1) {
            return 'red-cell';
        }
        else return '';
    };

    const getBoolean = (val)=>{
        return String(val)?.toLowerCase() === 'true';
    };

    const clearGridFilter = filter => {
        setRawDataGridDataState({...rawDataGridDataState, filter: filter ? filter : initialDataStateFilter});
    };

    return (
        <div className={classes.RawDataPanel}>
            <div style={{position: 'absolute',width: 0,height: 0}}>
                <svg xmlns="http://www.w3.org/2000/svg" version="1.1">
                    <defs>
                        <pattern
                            id="pattern2" width="3" height="3"
                            patternTransform="rotate(45 0 0)" patternUnits="userSpaceOnUse"
                        >
                            <line
                                x1="0" y1="0" x2="0"
                                y2="10" style={{stroke: '#8d8d8d',strokeWidth: 1}}
                            />
                        </pattern>
                    </defs>
                </svg>
            </div>
            <div className={classes.TableContainer}>
                <ExcelExport data={filterBy(rawData, rawDataGridDataState.filter)} ref={_export}>
                    {
                        exportColumnNames.map((column, index) => (
                            <ExcelExportColumn
                                key={index}
                                field={column.field}
                                title={column.name}
                                filter={column.filter}
                                locked={column.locked ? column.locked : false}
                            />
                        ))
                    }
                </ExcelExport>
                {
                    isEmpty(selectedRule) ?
                        <NoSelectedSensorsScreen
                            style={{
                                width:      '100%',
                                margin:     '0',
                                marginLeft: '1rem'
                            }}
                            message="Please select a Rule from the panel to the left to load the data"
                            height={classes.KendoGrid.height}
                        />
                        :
                        <React.Fragment>
                            <Grid
                                ref={rawDataGridRef}
                                style={{
                                    width:      '100%',
                                    margin:     '0',
                                    marginLeft: '1rem'
                                }}
                                className={classes.KendoGrid}
                                filterable={workbookModeEnabled}
                                sortable
                                reorderable
                                data={kendoProcess(rawData, rawDataGridDataState)}
                                {...rawDataGridDataState}
                                onDataStateChange={onRawDataGridDataStateChange}
                                onColumnReorder={onColumnReorder}
                            >
                                {
                                    workbookModeEnabled ?
                                        <GridToolbar>
                                            <FilterPanel
                                                title={'Raw Filter Builder'}
                                                filter={rawDataGridDataState.filter}
                                                filterText={gridFilterText}
                                                fields={rawDataGridFields}
                                                columns={columnNames}
                                                setFilter={clearGridFilter}
                                                setFilterText={setGridFilterText}
                                            />
                                        </GridToolbar>
                                        : null
                                }
                                {
                                    bobLoading.includes('FetchHealthScoreRawData') ?
                                        <GridNoRecords>
                                            Loading raw data
                                        </GridNoRecords>
                                        :
                                        null
                                }
                                {
                                    columnNames.map((column, index) => (
                                        <Column
                                            key={index}
                                            reorderable={column.field !== 'start' && column.field !== 'end' && !column.fault}
                                            locked={column.field === 'start' || column.field === 'end'}
                                            width={column.field === 'start' || column.field === 'end' ? 225 : column.field === 'score' || column.fault ? 175 : 250}
                                            field={column.field}
                                            title={column.name}
                                            filter={column.filter}
                                            filterCell={column.fault ? BuildFaultCellFilterCell : null}
                                            columnMenu={(props) => (
                                                <ColumnMenu
                                                    {...props}
                                                    isFormula={column.isFormula ? column.isFormula : false}
                                                    isFault={column.isFault ? column.isFault : false}
                                                />
                                            )}
                                            cell={column.field === 'start' || column.field === 'end' ? timeStampCell : column.fault ? faultCell : valueCell}
                                            headerCell={valueHeaderCell}
                                            fault={column.fault}
                                        />
                                    ))
                                }
                            </Grid>
                        </React.Fragment>
                }
                {
                    bobLoading.includes('FetchHealthScoreRawData') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
            { showModal?
                <ConditionalFormatModal
                    open={showModal}
                    formatList ={formatList}
                    formatKey = {formatKey}
                    addItem={addItem}
                    removeItem={removeItem}
                    fieldType={formatType}
                    updateFormat={updateFormat}
                    submitFormat={handleSubmit}
                    close={handleClose}
                />:''
            }
            {
                formulaCellModalOpen ?
                    <FormulaGridColumnModal
                        cellIndex={formulaColumnIndex}
                        columnList={columnNames.filter(c => c.filter === 'numeric')}
                        formula={currentFormula}
                        setFormulaFormIsOpen={setFormulaCellModalOpen}
                        setFormulaValues={setFormulaValues}
                        title={currentFormula ? 'Edit Formula' : 'Create Formula'}
                    />
                    : null
            }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeNavCompany:        state.bob.activeNavCompany,
        activeNavEquipment:      state.bob.activeNavEquipment,
        activeNavSite:           state.bob.activeNavSite,
        bobLoading:              state.bob.bobLoading,
        currentUser:             state.currentUser,
        dashboardEndTimeStamp:   state.dashboard.dashboardEndTimeStamp,
        dashboardStartTimeStamp: state.dashboard.dashboardStartTimeStamp,
        desiredInterval:         state.dashboard.desiredInterval,
        entityScoringData:       state.bob.entityScoringData,
        entityScoresData:        state.bob.entityScoresData,
        newConfiguration:        state.configurationData.newConfiguration,
        rawUnits:                state.architect.rawUnits,
        redirection:             state.configurationData.redirection,
        ruleScoreKpis:           state.dashboard.ruleScoreKpis,
        ruleScoringConfig:       state.dashboard.ruleScoringConfig,
        selectedRule:            state.dashboard.selectedRule,
        selectedWeatherSensors:  state.dashboard.selectedWeatherSensors,
        workbookModeEnabled:     state.dashboard.workbookModeEnabled
    };
};

const mapDispatchToProps = {
    ...BobActions,
    ...ConfigurationActions,
    ...DashboardActions
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RawDataTable));
